import { useCallback, useEffect, useState } from "react";
import { MessageCircleQuestion, Plus, Pencil, Trash2, Eye, EyeOff, X, Inbox, CheckCircle2, BarChart3 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import api from "../lib/api";

/* Admin curated Q&A manager for "Ask a Question": owner writes instant answers
   (markdown) shown without any LLM call. Drafts stay hidden until activated.
   Includes visitor-requested questions inbox with one-click approval of AI drafts.
   Moderator-only. */

const CATEGORIES = ["Airport Basics", "Security & Programs", "Flights & Disruptions", "Baggage",
  "Documents & Visas", "Booking & Tickets", "Travel Tips", "General"];

const EMPTY = { question: "", answer: "", keywords: "", category: "General", active: false };

export default function AdminAnswers() {
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(null); // {id?} + form fields
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(() => Promise.all([
    api.get("/ask/curated").then((r) => setItems(r.data.items)),
    api.get("/ask/requests").then((r) => setRequests(r.data.items)),
  ]).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const openEdit = (c) => {
    setPreview(false);
    setEditing(c
      ? { id: c.id, question: c.question, answer: c.answer || "", keywords: (c.keywords || []).join(", "), category: c.category || "General", active: !!c.active }
      : { ...EMPTY });
  };

  const approveDraft = async (draftId) => {
    try {
      await api.post(`/ask/curated/${draftId}/approve`);
      toast.success("Published! It's now live on the FAQ page and in Ask a Question");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
  };

  const dismissRequest = async (rid) => {
    try { await api.delete(`/ask/requests/${rid}`); load(); } catch { toast.error("Failed"); }
  };

  const save = async () => {
    const body = {
      question: editing.question.trim(),
      answer: editing.answer,
      keywords: editing.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      category: editing.category || "General",
      active: editing.active,
    };
    if (body.question.length < 4) { toast.error("Question is too short"); return; }
    if (body.active && !body.answer.trim()) { toast.error("Write the answer before activating"); return; }
    setBusy(true);
    try {
      if (editing.id) await api.put(`/ask/curated/${editing.id}`, body);
      else await api.post("/ask/curated", body);
      toast.success(editing.id ? "Answer saved" : "Question added");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const toggleActive = async (c) => {
    if (!c.active && !(c.answer || "").trim()) { toast.error("Write the answer first, then activate"); openEdit(c); return; }
    try {
      await api.put(`/ask/curated/${c.id}`, { question: c.question, answer: c.answer || "", keywords: c.keywords || [], category: c.category || "General", active: !c.active });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.question}"?`)) return;
    try { await api.delete(`/ask/curated/${c.id}`); load(); } catch { toast.error("Failed"); }
  };

  const live = items.filter((c) => c.active).sort((a, b) => (b.views || 0) - (a.views || 0));
  const drafts = items.filter((c) => !c.active);
  const openRequests = requests.filter((r) => r.status !== "published");

  const List = ({ title, list, badgeCls }) => (
    <section className="mb-8">
      <h2 className="font-heading font-900 text-xl mb-3">{title} <span className="text-sm font-500 text-muted-foreground">({list.length})</span></h2>
      {list.length === 0 ? <p className="text-sm text-muted-foreground">Nothing here.</p> : (
        <div className="space-y-2">
          {list.map((c) => (
            <div key={c.id} className="border border-zinc-300 bg-white p-3 flex flex-wrap items-center gap-2" data-testid={`curated-row-${c.id}`}>
              <span className={`text-[10px] font-900 tracking-widest px-1.5 py-0.5 ${badgeCls}`}>{c.active ? "LIVE" : "DRAFT"}</span>
              {c.ai_generated && <span className="text-[10px] font-900 tracking-widest px-1.5 py-0.5 bg-sky-100 text-sky-700" data-testid={`curated-ai-badge-${c.id}`}>AI — REVIEW</span>}
              <span className="text-[10px] font-700 tracking-wide px-1.5 py-0.5 bg-zinc-100 text-zinc-600">{c.category || "General"}</span>
              <span className="font-600 text-sm flex-1 min-w-[200px]">{c.question}</span>
              {c.active && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Times read (FAQ + Ask a Question)" data-testid={`curated-views-${c.id}`}><BarChart3 className="w-3.5 h-3.5" />{c.views || 0}</span>}
              {(c.keywords || []).length > 0 && <span className="text-xs text-muted-foreground hidden md:inline">{c.keywords.slice(0, 3).join(" · ")}</span>}
              <button onClick={() => openEdit(c)} data-testid={`curated-edit-${c.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-violet-700 hover:underline"><Pencil className="w-3.5 h-3.5" /> Edit</button>
              <button onClick={() => toggleActive(c)} data-testid={`curated-toggle-${c.id}`} className={`inline-flex items-center gap-1 text-xs font-700 hover:underline ${c.active ? "text-zinc-500" : "text-emerald-700"}`}>
                {c.active ? <><EyeOff className="w-3.5 h-3.5" /> Unpublish</> : <><Eye className="w-3.5 h-3.5" /> Publish</>}
              </button>
              <button onClick={() => remove(c)} data-testid={`curated-delete-${c.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-rose-600 hover:underline"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-answers">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
          <MessageCircleQuestion className="w-9 h-9 text-violet-700" /> Curated Answers
        </h1>
        <button onClick={() => openEdit(null)} data-testid="curated-new-btn"
          className="inline-flex items-center gap-1.5 bg-violet-700 text-white px-4 py-2.5 text-sm font-700 hover:bg-violet-600">
          <Plus className="w-4 h-4" /> New question
        </button>
      </div>
      <p className="text-muted-foreground mb-8 max-w-2xl">
        Owner-written answers for "Ask a Question". Published answers show <strong>instantly</strong> — no AI call —
        with a Google search link at the bottom. Drafts stay hidden until you write the answer and publish.
        Keywords help match different phrasings (e.g. "get to the airport" → the arrival-time answer).
      </p>

      <section className="mb-8" data-testid="requests-inbox">
        <h2 className="font-heading font-900 text-xl mb-3 flex items-center gap-2">
          <Inbox className="w-5 h-5 text-violet-700" /> Requested by travelers
          <span className="text-sm font-500 text-muted-foreground">({openRequests.length})</span>
        </h2>
        {openRequests.length === 0 ? <p className="text-sm text-muted-foreground">No open requests — visitors' unanswered FAQ searches will appear here.</p> : (
          <div className="space-y-2">
            {openRequests.map((r) => (
              <div key={r.id} className="border border-violet-200 bg-violet-50/50 p-3 flex flex-wrap items-center gap-2" data-testid={`request-row-${r.id}`}>
                <span className={`text-[10px] font-900 tracking-widest px-1.5 py-0.5 ${r.status === "drafted" ? "bg-sky-100 text-sky-700" : r.status === "llm_unavailable" ? "bg-rose-100 text-rose-700" : "bg-zinc-200 text-zinc-600"}`}>
                  {r.status === "drafted" ? "AI DRAFT READY" : r.status === "llm_unavailable" ? "NEEDS MANUAL ANSWER" : "QUEUED"}
                </span>
                <span className="font-600 text-sm flex-1 min-w-[200px]">{r.question}</span>
                <span className="text-xs text-muted-foreground hidden sm:inline">{(r.requested_at || "").slice(0, 10)}</span>
                {r.draft_id && (
                  <>
                    <button onClick={() => { const d = items.find((c) => c.id === r.draft_id); if (d) openEdit(d); }}
                      data-testid={`request-review-${r.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-violet-700 hover:underline"><Pencil className="w-3.5 h-3.5" /> Review</button>
                    <button onClick={() => approveDraft(r.draft_id)} data-testid={`request-approve-${r.id}`}
                      className="inline-flex items-center gap-1 bg-emerald-700 text-white px-2.5 py-1 text-xs font-700 hover:bg-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> Approve & publish</button>
                  </>
                )}
                {!r.draft_id && r.status !== "queued" && (
                  <button onClick={() => openEdit({ ...EMPTY, question: r.question })} data-testid={`request-write-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs font-700 text-violet-700 hover:underline"><Plus className="w-3.5 h-3.5" /> Write answer</button>
                )}
                <button onClick={() => dismissRequest(r.id)} data-testid={`request-dismiss-${r.id}`}
                  className="inline-flex items-center gap-1 text-xs font-700 text-rose-600 hover:underline"><Trash2 className="w-3.5 h-3.5" /> Dismiss</button>
              </div>
            ))}
          </div>
        )}
      </section>

      <List title="Live answers (sorted by most read)" list={live} badgeCls="bg-emerald-100 text-emerald-700" />
      <List title="Drafts — write these when you have time" list={drafts} badgeCls="bg-amber-100 text-amber-700" />

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="curated-editor">
          <div className="bg-white border border-zinc-300 w-full max-w-3xl my-8">
            <div className="flex items-center justify-between px-5 py-3 bg-violet-700 text-white">
              <span className="font-heading font-900">{editing.id ? "Edit answer" : "New question"}</span>
              <button onClick={() => setEditing(null)} data-testid="curated-editor-close" className="p-1 hover:bg-violet-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-700 uppercase tracking-wide text-zinc-500">Question</label>
                <input value={editing.question} onChange={(e) => setEditing((s) => ({ ...s, question: e.target.value }))}
                  data-testid="curated-question-input"
                  className="w-full border border-zinc-300 px-3 py-2.5 text-sm mt-1 outline-none focus:ring-2 focus:ring-violet-500" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-700 uppercase tracking-wide text-zinc-500">Answer (markdown: **bold**, [links](https://...), - bullets)</label>
                  <button onClick={() => setPreview((p) => !p)} data-testid="curated-preview-toggle" className="text-xs font-700 text-violet-700 hover:underline">{preview ? "Edit" : "Preview"}</button>
                </div>
                {preview ? (
                  <div className="border border-zinc-200 bg-zinc-50 p-4 mt-1 text-sm prose prose-sm max-w-none min-h-[220px]" data-testid="curated-answer-preview">
                    <ReactMarkdown>{editing.answer || "*Nothing yet*"}</ReactMarkdown>
                  </div>
                ) : (
                  <textarea value={editing.answer} onChange={(e) => setEditing((s) => ({ ...s, answer: e.target.value }))}
                    rows={12} data-testid="curated-answer-input"
                    className="w-full border border-zinc-300 px-3 py-2.5 text-sm mt-1 font-mono outline-none focus:ring-2 focus:ring-violet-500" />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-700 uppercase tracking-wide text-zinc-500">Category</label>
                  <select value={editing.category} onChange={(e) => setEditing((s) => ({ ...s, category: e.target.value }))}
                    data-testid="curated-category-select"
                    className="w-full border border-zinc-300 px-3 py-2.5 text-sm mt-1 bg-white outline-none focus:ring-2 focus:ring-violet-500">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-700 uppercase tracking-wide text-zinc-500">Match keywords (comma-separated phrases)</label>
                  <input value={editing.keywords} onChange={(e) => setEditing((s) => ({ ...s, keywords: e.target.value }))}
                    data-testid="curated-keywords-input" placeholder="how early, get to the airport, arrive early"
                    className="w-full border border-zinc-300 px-3 py-2.5 text-sm mt-1 outline-none focus:ring-2 focus:ring-violet-500" />
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
                <label className="flex items-center gap-2 text-sm font-600 cursor-pointer">
                  <input type="checkbox" checked={editing.active} onChange={(e) => setEditing((s) => ({ ...s, active: e.target.checked }))}
                    data-testid="curated-active-checkbox" className="w-4 h-4 accent-violet-700" />
                  Published (visible to travelers)
                </label>
                <button onClick={save} disabled={busy} data-testid="curated-save-btn"
                  className="bg-emerald-700 text-white px-5 py-2.5 text-sm font-700 hover:bg-emerald-600 disabled:opacity-50">
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
